import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
// eslint-disable-next-line no-unused-vars
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { FaArrowLeft, FaBed, FaBath, FaRulerCombined, FaCar, FaHome, FaAngleDown, FaCheck, FaChevronUp, FaChevronDown, FaHeart, FaShoppingCart } from 'react-icons/fa'
import { useCart } from '../../context/CartContext'

export default function StoreItemDetails() {
    const { planNumber } = useParams()
    const { t, i18n } = useTranslation()
    const currentLang = i18n.language
    const { addItem } = useCart()

    const [item, setItem] = useState(null)
    const [loading, setLoading] = useState(true)
    const [activeTab, setActiveTab] = useState('specs') // specs | features | included
    const [selectedImageIndex, setSelectedImageIndex] = useState(0)
    const [likedPlans, setLikedPlans] = useState(() => JSON.parse(localStorage.getItem('rwg_liked_plans') || '[]'))

    const toggleLike = () => {
        if (!item) return
        setLikedPlans(prev => {
            const next = prev.includes(item.plan_number)
                ? prev.filter(p => p !== item.plan_number)
                : [...prev, item.plan_number]
            localStorage.setItem('rwg_liked_plans', JSON.stringify(next))
            return next
        })
    }

    const gallery = item ? [item.image_url, ...(item.image_gallery || [])].filter(Boolean) : []

    useEffect(() => {
        const fetchItem = async () => {
            setLoading(true)
            const { data, error } = await supabase
                .from('store_items')
                .select('*')
                .eq('plan_number', planNumber)
                .eq('is_published', true)
                .single()

            if (data) {
                setItem(data)
            } else {
                console.error('Error fetching plan:', error)
            }
            setLoading(false)
        }
        fetchItem()
    }, [planNumber])

    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(price)
    }

    const getLocalizedText = (enText, esText) => {
        if (currentLang === 'es' && esText) return esText
        return enText || ''
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-cloud pt-24 pb-16 flex justify-center items-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
            </div>
        )
    }

    if (!item) {
        return (
            <div className="min-h-screen bg-cloud pt-24 pb-16 text-center px-4">
                <h1 className="text-3xl font-bold text-ink mb-4">{t('admin.store.plan_not_found')}</h1>
                <p className="text-ash mb-8">{t('admin.store.plan_not_found_desc')}</p>
                <Link to="/store" className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition">
                    <FaArrowLeft /> {t('admin.store.view_all_plans')}
                </Link>
            </div>
        )
    }

    const renderCommaList = (text) => {
        if (!text || typeof text !== 'string') return null
        const items = text.split(',').map(s => s.trim()).filter(Boolean)
        if (items.length === 0) return null
        return (
            <ul className="space-y-3">
                {items.map((str, idx) => (
                    <li key={idx} className="flex flex-start gap-3 text-ink">
                        <FaCheck className="text-primary mt-1 shrink-0" />
                        <span>{str}</span>
                    </li>
                ))}
            </ul>
        )
    }

    return (
        <div className="min-h-screen bg-cloud pt-20 pb-16">
            {/* Nav */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6 mt-4">
                <Link to="/store" className="inline-flex items-center gap-2 text-sm text-ash hover:text-primary transition-colors font-medium">
                    <FaArrowLeft /> {t('admin.store.back_to_plans')}
                </Link>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">

                    {/* Main Image Section */}
                    <div className="lg:col-span-8">
                        <div className="bg-white p-2 rounded-2xl shadow-sm border border-gray-100 mb-8 relative flex flex-col md:flex-row gap-2">
                            {gallery.length > 0 ? (
                                <>
                                    <div className="flex-1 overflow-hidden rounded-xl relative aspect-[16/10] bg-gray-50">
                                        <div className="absolute top-4 left-0 bg-gray-500/80 backdrop-blur text-white px-4 py-1.5 font-bold uppercase tracking-wider text-sm rounded-r-lg z-10 shadow-sm">
                                            PLAN {item.plan_number || '123-1234'}
                                        </div>
                                        <button
                                            onClick={toggleLike}
                                            className="absolute top-4 right-4 backdrop-blur rounded-full p-2.5 z-10 transition bg-black/40 hover:bg-black/60"
                                        >
                                            <FaHeart className={likedPlans.includes(item?.plan_number) ? 'text-red-500' : 'text-white'} />
                                        </button>
                                        <img src={gallery[selectedImageIndex]} className="w-full h-full object-contain transition-opacity duration-300" alt={getLocalizedText(item.title_en, item.title_es)} />
                                    </div>
                                    {gallery.length > 1 && (
                                        <div className="w-full md:w-32 flex flex-row md:flex-col justify-between items-center gap-2">
                                            <button onClick={() => setSelectedImageIndex(prev => prev > 0 ? prev - 1 : gallery.length - 1)} className="text-gray-400 hover:text-ink transition p-1 hidden md:block">
                                                <FaChevronUp className="text-xl" />
                                            </button>
                                            <div className="flex-1 w-full flex flex-row md:flex-col gap-2 overflow-x-auto md:overflow-y-auto custom-scrollbar p-1">
                                                {gallery.map((imgUrl, idx) => (
                                                    <button key={idx} onClick={() => setSelectedImageIndex(idx)} className={`w-24 md:w-full shrink-0 aspect-[4/3] rounded-lg overflow-hidden border-2 transition bg-gray-50 ${selectedImageIndex === idx ? 'border-primary' : 'border-transparent opacity-60 hover:opacity-100'}`}>
                                                        <img src={imgUrl} className="w-full h-full object-contain" alt="Thumbnail" />
                                                    </button>
                                                ))}
                                            </div>
                                            <button onClick={() => setSelectedImageIndex(prev => prev < gallery.length - 1 ? prev + 1 : 0)} className="text-gray-400 hover:text-ink transition p-1 hidden md:block">
                                                <FaChevronDown className="text-xl" />
                                            </button>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="w-full aspect-[16/10] bg-gray-100 flex items-center justify-center text-gray-400 rounded-xl">
                                    {t('admin.store.no_image')}
                                </div>
                            )}
                        </div>

                        {/* Tabs content */}
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8">
                            <div className="flex flex-wrap gap-4 border-b border-gray-200 mb-8">
                                {['specs', 'features', 'included'].map(tab => (
                                    <button
                                        key={tab}
                                        onClick={() => setActiveTab(tab)}
                                        className={`pb-4 px-2 text-sm md:text-base font-bold uppercase tracking-wider transition-colors relative ${activeTab === tab ? 'text-primary' : 'text-gray-400 hover:text-ink'}`}
                                    >
                                        {tab === 'specs' && t('admin.store.preview_view.specs_dimensions')}
                                        {tab === 'features' && t('admin.store.preview_view.features')}
                                        {tab === 'included' && t('admin.store.preview_view.whats_included')}

                                        {activeTab === tab && (
                                            <motion.div layoutId="activeTab" className="absolute bottom-[-1px] left-0 right-0 h-[2px] bg-primary" />
                                        )}
                                    </button>
                                ))}
                            </div>

                            <AnimatePresence mode="wait">
                                {activeTab === 'specs' && (
                                    <motion.div key="specs" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-8">

                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                                            <div className="bg-gray-50 p-4 rounded-xl text-center">
                                                <p className="text-gray-500 text-sm mb-1">{t('admin.store.sqft')}</p>
                                                <p className="text-xl font-bold text-ink">{item.sq_ft || 0}</p>
                                            </div>
                                            <div className="bg-gray-50 p-4 rounded-xl text-center">
                                                <p className="text-gray-500 text-sm mb-1">{t('admin.store.preview_view.dimensions_short') || 'Dimensions'}</p>
                                                <p className="text-xl font-bold text-ink">{item.width_ft || 0}' {item.width_in || 0}" W<br /><span className="text-sm font-normal">x</span> {item.depth_ft || 0}' {item.depth_in || 0}" D</p>
                                            </div>
                                            <div className="bg-gray-50 p-4 rounded-xl text-center">
                                                <p className="text-gray-500 text-sm mb-1">{t('admin.store.preview_view.height_short') || 'Height'}</p>
                                                <p className="text-xl font-bold text-ink">{item.height_ft || 0}' {item.height_in || 0}"</p>
                                            </div>
                                            <div className="bg-gray-50 p-4 rounded-xl text-center">
                                                <p className="text-gray-500 text-sm mb-1">{t('admin.store.stories')}</p>
                                                <p className="text-xl font-bold text-ink">{item.stories || 0}</p>
                                            </div>
                                        </div>

                                        {((item.main_floor_sq_ft > 0 || item.upper_floor_sq_ft > 0 || item.lower_floor_sq_ft > 0 || item.basement_sq_ft > 0) || (item.main_ceiling_ft > 0 || item.upper_ceiling_ft > 0)) && (
                                            <div className="grid md:grid-cols-2 gap-8">
                                                {/* Area Breakdown */}
                                                {(item.main_floor_sq_ft > 0 || item.upper_floor_sq_ft > 0 || item.lower_floor_sq_ft > 0 || item.basement_sq_ft > 0) && (
                                                    <div>
                                                        <h3 className="text-lg font-bold text-ink mb-4 border-b pb-2">{t('admin.store.preview_view.area_breakdown')}</h3>
                                                        <ul className="space-y-3">
                                                            {item.main_floor_sq_ft > 0 && <li className="flex justify-between items-center"><span className="text-ash">{t('admin.store.main_floor')}:</span><span className="font-semibold text-ink">{item.main_floor_sq_ft} sq ft</span></li>}
                                                            {item.upper_floor_sq_ft > 0 && <li className="flex justify-between items-center"><span className="text-ash">{t('admin.store.upper_floor')}:</span><span className="font-semibold text-ink">{item.upper_floor_sq_ft} sq ft</span></li>}
                                                            {item.lower_floor_sq_ft > 0 && <li className="flex justify-between items-center"><span className="text-ash">{t('admin.store.lower_floor')}:</span><span className="font-semibold text-ink">{item.lower_floor_sq_ft} sq ft</span></li>}
                                                            {item.basement_sq_ft > 0 && <li className="flex justify-between items-center"><span className="text-ash">{t('admin.store.basement')}:</span><span className="font-semibold text-ink">{item.basement_sq_ft} sq ft</span></li>}
                                                        </ul>
                                                    </div>
                                                )}

                                                {/* Ceiling Heights */}
                                                {(item.main_ceiling_ft > 0 || item.upper_ceiling_ft > 0) && (
                                                    <div>
                                                        <h3 className="text-lg font-bold text-ink mb-4 border-b pb-2">{t('admin.store.preview_view.ceiling_heights')}</h3>
                                                        <ul className="space-y-3">
                                                            {item.main_ceiling_ft > 0 && <li className="flex justify-between items-center"><span className="text-ash">{t('admin.store.main_ceiling')}:</span><span className="font-semibold text-ink">{item.main_ceiling_ft}'</span></li>}
                                                            {item.upper_ceiling_ft > 0 && <li className="flex justify-between items-center"><span className="text-ash">{t('admin.store.upper_ceiling')}:</span><span className="font-semibold text-ink">{item.upper_ceiling_ft}'</span></li>}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                    </motion.div>
                                )}

                                {activeTab === 'features' && (
                                    <motion.div key="features" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-8">
                                        <div className="grid md:grid-cols-2 gap-8">
                                            <div>
                                                <h3 className="font-bold text-ink mb-4 border-b pb-2">{t('admin.store.bed_features')}</h3>
                                                {renderCommaList(getLocalizedText(item.bedroom_features_en, item.bedroom_features_es)) || <p className="text-ash italic">{t('admin.store.preview_view.not_specified')}</p>}
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-ink mb-4 border-b pb-2">{t('admin.store.kitchen_features')}</h3>
                                                {renderCommaList(getLocalizedText(item.kitchen_features_en, item.kitchen_features_es)) || <p className="text-ash italic">{t('admin.store.preview_view.not_specified')}</p>}
                                            </div>
                                        </div>

                                        {(item.roof_pitch || item.roof_load > 0 || item.roof_framing || item.exterior_wall_finish || item.framing) && (
                                            <div>
                                                <h3 className="font-bold text-ink mb-4 border-b pb-2">{t('admin.store.preview_view.construction_details')}</h3>
                                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-4 gap-x-8 text-sm">
                                                    {item.roof_pitch && <div><span className="block text-gray-500 mb-1">{t('admin.store.roof_pitch')}</span><span className="font-semibold text-ink">{item.roof_pitch}</span></div>}
                                                    {item.roof_load > 0 && <div><span className="block text-gray-500 mb-1">{t('admin.store.roof_load')}</span><span className="font-semibold text-ink">{item.roof_load} PSF</span></div>}
                                                    {item.roof_framing && <div><span className="block text-gray-500 mb-1">{t('admin.store.roof_framing')}</span><span className="font-semibold text-ink">{item.roof_framing}</span></div>}
                                                    {item.exterior_wall_finish && <div><span className="block text-gray-500 mb-1">{t('admin.store.exterior_wall')}</span><span className="font-semibold text-ink">{item.exterior_wall_finish}</span></div>}
                                                    {item.framing && <div><span className="block text-gray-500 mb-1">{t('admin.store.framing')}</span><span className="font-semibold text-ink">{item.framing}</span></div>}
                                                </div>
                                            </div>
                                        )}
                                    </motion.div>
                                )}

                                {activeTab === 'included' && (
                                    <motion.div key="included" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                                        <h3 className="font-bold text-ink mb-4 border-b pb-2">{t('admin.store.preview_view.plan_set_contents')}</h3>
                                        <p className="text-ash mb-6">{t('admin.store.preview_view.plan_set_desc')}</p>
                                        <div className="bg-gray-50 p-6 rounded-xl border border-gray-100">
                                            {renderCommaList(getLocalizedText(item.included_in_plan_set_en, item.included_in_plan_set_es)) || <p className="text-ash italic">{t('admin.store.preview_view.details_not_available')}</p>}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>

                    {/* Sidebar / Purchasing Card */}
                    <div className="lg:col-span-4">
                        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 sticky top-28 p-6 md:p-8">
                            <span className="inline-block px-3 py-1 bg-gray-100 text-gray-600 font-bold text-xs uppercase tracking-widest rounded-full mb-4">
                                Plan {item.plan_number || 'TBD'}
                            </span>

                            <h1 className="text-3xl font-bold text-ink mb-4 leading-tight">
                                {getLocalizedText(item.title_en, item.title_es) || t('admin.store.preview_view.custom_plan')}
                            </h1>

                            <div className="flex items-center gap-4 mb-6">
                                <span className="text-4xl font-extrabold text-primary">{formatPrice(item.price)}</span>
                                {item.is_on_sale && item.sale_price > 0 && (
                                    <span className="text-xl text-gray-400 line-through font-medium">{formatPrice(item.sale_price)}</span>
                                )}
                            </div>

                            <p className="text-ash mb-8">
                                {getLocalizedText(item.description_en, item.description_es) || t('admin.store.preview_view.no_desc')}
                            </p>

                            <div className="grid grid-cols-2 gap-4 mb-8">
                                <div className="flex items-center gap-3">
                                    <div className="bg-primary/10 p-3 rounded-xl text-primary"><FaRulerCombined className="text-xl" /></div>
                                    <div>
                                        <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">{t('admin.store.preview_view.area')}</p>
                                        <p className="font-semibold text-ink">{item.sq_ft || 0} sq ft</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="bg-primary/10 p-3 rounded-xl text-primary"><FaBed className="text-xl" /></div>
                                    <div>
                                        <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">{t('admin.store.beds')}</p>
                                        <p className="font-semibold text-ink">{item.beds || 0}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="bg-primary/10 p-3 rounded-xl text-primary"><FaBath className="text-xl" /></div>
                                    <div>
                                        <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">{t('admin.store.baths')}</p>
                                        <p className="font-semibold text-ink">{item.baths || 0}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="bg-primary/10 p-3 rounded-xl text-primary"><FaCar className="text-xl" /></div>
                                    <div>
                                        <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">{t('admin.store.garages')}</p>
                                        <p className="font-semibold text-ink">{item.garages || 0}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                {item.stripe_payment_link_url ? (
                                    <a
                                        href={item.stripe_payment_link_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center justify-center gap-3 w-full py-4 bg-[#635bff] text-white font-bold rounded-xl shadow-lg hover:shadow-[#635bff]/30 hover:-translate-y-1 transition-all duration-300"
                                    >
                                        <FaShoppingCart className="w-5 h-5" />
                                        {t('admin.store.preview_view.purchase_plan')}
                                    </a>
                                ) : (
                                    <button
                                        onClick={() => addItem({
                                            id: item.id,
                                            name: getLocalizedText(item.title_en, item.title_es) || `Plan ${item.plan_number}`,
                                            planNumber: item.plan_number,
                                            price: item.price || 0,
                                        })}
                                        className="flex items-center justify-center gap-3 w-full py-4 bg-primary text-white font-bold rounded-xl shadow-lg hover:shadow-primary/30 hover:-translate-y-1 transition-all duration-300"
                                    >
                                        <FaShoppingCart className="w-5 h-5" />
                                        {t('admin.store.preview_view.purchase_plan')}
                                    </button>
                                )}
                                <Link to="/contact" className="block w-full py-4 bg-white border-2 border-gray-200 text-ink text-center font-bold rounded-xl hover:border-primary hover:text-primary transition-all duration-300">
                                    {t('admin.store.preview_view.contact_specialist')}
                                </Link>
                            </div>

                            <p className="text-xs text-gray-400 text-center mt-6">
                                Secure checkout. Plans delivered instantly in PDF format upon payment.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
